function cleanText(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function toneForAgingBucket(bucket, firstResponseOverdue) {
  if (firstResponseOverdue || bucket === "overdue") {
    return "overdue";
  }
  if (bucket === "stale") {
    return "stale";
  }
  if (bucket === "new") {
    return "ready";
  }
  return "muted";
}

function queueLabel(queueKind) {
  return {
    focus: "Focus queue",
    mine: "My assigned queue",
    open: "Open queue",
    overdue: "Overdue queue",
    unassigned: "Unassigned queue",
  }[queueKind] || "Operator queue";
}

export function buildOperatorLeadCardView(item = {}, options = {}) {
  const settings = Object.assign({ queueKind: "mine", canClaim: false }, options);
  const agingBucket = cleanText(item.aging_bucket, "n/a");
  const assigned = Boolean(item.assigned_user_id);
  const firstResponseOverdue = Boolean(item.first_response_overdue);
  const queueKind = settings.queueKind || "mine";
  const claimable = queueKind === "unassigned" && settings.canClaim && !assigned;
  const urgencyTone = toneForAgingBucket(agingBucket, firstResponseOverdue);
  const latestMessage = cleanText(item.latest_message?.body, "No latest message.");
  const ownerLabel = assigned ? "Assigned" : "Unassigned";
  const urgencyLabel = firstResponseOverdue
    ? "Reply now"
    : agingBucket === "overdue"
      ? "Overdue"
      : agingBucket === "stale"
        ? "Needs touch"
        : agingBucket === "new"
          ? "New lead"
          : "Review";

  const nextAction = claimable
    ? "Claim lead"
    : firstResponseOverdue
      ? "Reply now"
      : assigned
        ? "Continue lead"
        : "Review lead";
  const priorityLabel = firstResponseOverdue
    ? "First reply overdue"
    : agingBucket === "overdue"
      ? "SLA overdue"
      : agingBucket === "stale"
        ? "Needs touch"
        : urgencyLabel;
  const focusTitle = claimable
    ? "Claim before working"
    : firstResponseOverdue
      ? "Reply lane is first"
      : assigned
        ? "Assigned lead ready"
        : "Review lead context";
  const focusDetail = claimable
    ? "Claiming creates ownership before reply and disposition work."
    : firstResponseOverdue
      ? "Open the workspace and queue a sandbox reply before disposition."
      : assigned
        ? "Continue through reply, outcome, follow-up, and visibility."
        : "Open the workspace to review messages and record context.";

  return {
    contactName: cleanText(item.contact_name, "Unknown contact"),
    phone: cleanText(item.phone_e164, "phone unavailable"),
    queueLabel: queueLabel(queueKind),
    latestMessage,
    latestMessagePreview: latestMessage.length > 150 ? `${latestMessage.slice(0, 147)}...` : latestMessage,
    priorityLabel,
    priorityTone: urgencyTone,
    focusTitle,
    focusDetail,
    primaryAction: nextAction,
    openLabel: nextAction === "Claim lead" ? "Open" : nextAction,
    claimLabel: "Claim",
    canClaim: claimable,
    canUnassign: queueKind === "mine",
    tone: urgencyTone,
    badges: [
      { label: agingBucket, tone: urgencyTone },
      { label: firstResponseOverdue ? "response overdue" : "response ok", tone: firstResponseOverdue ? "overdue" : "ready" },
      { label: ownerLabel.toLowerCase(), tone: assigned ? "ready" : "stale" },
    ],
    facts: [
      { label: "Next action", value: nextAction, tone: claimable || firstResponseOverdue ? "overdue" : "ready" },
      { label: "Queue", value: queueLabel(queueKind), tone: claimable ? "stale" : "ready" },
      { label: "Last touch", value: cleanText(item.last_message_at || item.created_at, "timestamp unavailable"), tone: "muted" },
    ],
  };
}

export function buildOperatorMessagingGuard(options = {}) {
  const settings = Object.assign({ canReply: true }, options);
  const locked = !settings.canReply;
  return {
    title: "Sandbox messaging",
    statusLabel: locked ? "Locked" : "Test send only",
    tone: locked ? "muted" : "stale",
    detail: locked
      ? "Reply controls are locked for this record. Review the thread, recent activity, and disposition options."
      : "Replies from this workspace are treated as test/sandbox sends until production messaging is explicitly approved.",
    checklist: [
      "Verify the contact and thread context before queueing a reply.",
      "Use disposition and follow-up tasks to make the next owner action visible.",
      "Do not use this lane for production texting until messaging is promoted.",
    ],
  };
}

function normalizeStatus(value, fallback = "unknown") {
  return cleanText(value, fallback).toLowerCase().replaceAll("_", " ");
}

function toneForDeliveryStatus(status) {
  const normalized = normalizeStatus(status);
  if (["delivered", "received"].includes(normalized)) {
    return "ready";
  }
  if (["failed", "undelivered", "bounced", "error"].includes(normalized)) {
    return "overdue";
  }
  if (["queued", "accepted", "sent", "sending", "submitted"].includes(normalized)) {
    return "stale";
  }
  return "muted";
}

function latestOutboundMessage(messages = [], safety = null) {
  if (safety?.latest_outbound_message) {
    return safety.latest_outbound_message;
  }
  return [...(Array.isArray(messages) ? messages : [])]
    .reverse()
    .find((item) => String(item?.direction || "").toLowerCase() === "outbound") || null;
}

function latestOutboundEvents(safety = null) {
  return Array.isArray(safety?.latest_outbound_events) ? safety.latest_outbound_events : [];
}

function eventLabel(event) {
  const type = normalizeStatus(event?.event_type, "event");
  const status = normalizeStatus(event?.provider_status, "");
  return status ? `${type}: ${status}` : type;
}

function timelineIndicatesSuppression(timelineEvents = []) {
  return (Array.isArray(timelineEvents) ? timelineEvents : []).some((item) => {
    const eventType = String(item?.event_type || "").toLowerCase();
    const summary = String(item?.summary || "").toLowerCase();
    const disposition = String(item?.payload_json?.disposition || "").toLowerCase();
    return (
      eventType.includes("opted_out")
      || eventType.includes("suppression")
      || disposition === "do_not_contact"
      || summary.includes("do not contact")
      || summary.includes("opted out")
      || summary.includes("suppressed")
    );
  });
}

export function buildOperatorMessagingSafety(options = {}) {
  const settings = Object.assign(
    {
      canReply: true,
      conversation: null,
      messages: [],
      timelineEvents: [],
      latestMutation: null,
      safety: null,
    },
    options
  );
  const safety = settings.safety || {};
  const latestOutbound = latestOutboundMessage(settings.messages, safety);
  const events = latestOutboundEvents(safety);
  const latestEvent = events[0] || null;
  const latestStatus = latestOutbound ? normalizeStatus(latestOutbound.status, "queued") : "none";
  const readinessIssues = Array.isArray(safety.readiness_issues) ? safety.readiness_issues.filter(Boolean) : [];
  const liveMode = safety.send_mode === "live";
  const liveEnabled = Boolean(safety.live_sending_enabled);
  const suppressed =
    safety.can_queue_reply === false
    || Boolean(safety.suppression_reason)
    || timelineIndicatesSuppression(settings.timelineEvents)
    || (
      settings.latestMutation?.kind === "disposition"
      && settings.latestMutation?.disposition === "do_not_contact"
    );
  const phoneBlocked =
    safety.block_reason
    || (!settings.canReply ? "Reply controls are locked for this role or queue state." : null);
  const replyLocked = suppressed || Boolean(phoneBlocked);
  const deliveryTone = latestOutbound ? toneForDeliveryStatus(latestOutbound.status) : "muted";
  const deliveryDetail = latestOutbound
    ? [
        latestEvent ? `Latest event ${eventLabel(latestEvent)}` : null,
        latestOutbound.delivered_at ? `Delivered ${latestOutbound.delivered_at}` : null,
        latestOutbound.sent_at ? `Sent ${latestOutbound.sent_at}` : null,
        latestOutbound.error_code ? `Provider error ${latestOutbound.error_code}` : null,
      ].filter(Boolean).join(" | ") || "Waiting for provider status callback or worker completion."
    : "No outbound reply has been queued for this conversation yet.";
  const suppressionValue = suppressed
    ? cleanText(safety.suppression_reason, "do not contact / opt-out")
    : "Clear in current context";
  return {
    title: "Messaging safety readiness",
    statusLabel: replyLocked ? "Reply locked" : liveEnabled ? "Live ready" : "Sandbox ready",
    tone: replyLocked ? "overdue" : "ready",
    replyLocked,
    replyLockReason: cleanText(phoneBlocked || (suppressed ? "Contact is suppressed or marked do-not-contact." : ""), null),
    rows: [
      {
        label: "Send mode",
        value: liveEnabled ? "Live sending enabled" : liveMode ? "Live mode gated" : "Sandbox/test only",
        tone: liveEnabled ? "stale" : "ready",
        meta: liveEnabled
          ? "Live sends require explicit approval and monitoring."
          : liveMode
            ? "Runtime is set to live mode, but approval or prerequisites still block network sends."
            : "Production texting remains blocked until promoted intentionally.",
      },
      {
        label: "Live gate",
        value: liveEnabled ? "Approved" : "Locked to sandbox",
        tone: liveEnabled ? "stale" : "ready",
        meta: readinessIssues.length
          ? readinessIssues.join(" ")
          : liveEnabled
            ? "Live prerequisites are satisfied and runtime approval is enabled."
            : "Sandbox lock is active.",
      },
      {
        label: "Opt-out / DNC",
        value: suppressionValue,
        tone: suppressed ? "overdue" : "ready",
        meta: suppressed ? cleanText(safety.block_reason, "Future replies should stay blocked.") : "No visible suppression event on this record.",
      },
      {
        label: "Delivery state",
        value: latestOutbound ? latestStatus : "No outbound reply",
        tone: deliveryTone,
        meta: deliveryDetail,
      },
      {
        label: "Audit trail",
        value: events.length ? `${events.length} event${events.length === 1 ? "" : "s"}` : latestOutbound ? "Waiting for callback" : "Ready to track",
        tone: events.length ? deliveryTone : latestOutbound ? "stale" : "muted",
        meta: events.length
          ? `Latest audit event: ${eventLabel(latestEvent)}.`
          : latestOutbound
            ? "The reply is in the thread; worker/provider event history has not arrived yet."
            : "Queueing a reply will add message and timeline evidence for manager/owner review.",
      },
    ],
  };
}

export const QUALIFICATION_FIELDS = [
  { key: "motivation", label: "Motivation", placeholder: "Why are they considering selling?" },
  { key: "timeline", label: "Timeline", placeholder: "When do they want to make a decision?" },
  { key: "condition", label: "Property condition", placeholder: "Known repairs, occupancy, or site condition." },
  { key: "price_expectation", label: "Price expectation", placeholder: "Target price, range, or expectation." },
  { key: "next_step", label: "Next step", placeholder: "Call, review, offer, nurture, or manager review." },
];

function fieldByLabel(line) {
  const [rawLabel, ...rest] = String(line || "").split(":");
  if (!rest.length) {
    return null;
  }
  const normalized = rawLabel.trim().toLowerCase();
  const field = QUALIFICATION_FIELDS.find((item) => item.label.toLowerCase() === normalized);
  if (!field) {
    return null;
  }
  return { key: field.key, value: rest.join(":").trim() };
}

export function parseLeadQualificationNote(note) {
  const values = Object.fromEntries(QUALIFICATION_FIELDS.map((item) => [item.key, ""]));
  if (!note || note.note_type !== "qualification") {
    return values;
  }
  for (const line of String(note.body || "").split(/\r?\n/)) {
    const parsed = fieldByLabel(line);
    if (parsed) {
      values[parsed.key] = parsed.value;
    }
  }
  return values;
}

export function buildQualificationNoteBody(values = {}) {
  return QUALIFICATION_FIELDS.map((field) => `${field.label}: ${cleanText(values[field.key], "Not captured")}`).join("\n");
}

export function buildLeadQualificationWorkspace(options = {}) {
  const settings = Object.assign({ notes: [], available: true, unavailableReason: null }, options);
  const notes = Array.isArray(settings.notes) ? settings.notes : [];
  const latestQualification = notes.find((item) => item.note_type === "qualification") || null;
  const values = parseLeadQualificationNote(latestQualification);
  const capturedCount = QUALIFICATION_FIELDS.filter((field) => cleanText(values[field.key])).length;
  const totalCount = QUALIFICATION_FIELDS.length;
  const ready = capturedCount >= 4;
  const unavailable = settings.unavailableReason || "Qualification becomes available once this conversation is linked to a lead record.";

  return {
    fields: QUALIFICATION_FIELDS.map((field) => ({ ...field, value: values[field.key] || "" })),
    latestQualification,
    capturedCount,
    totalCount,
    statusLabel: settings.available ? (ready ? "Qualified" : "Needs detail") : "Unavailable",
    tone: settings.available ? (ready ? "ready" : capturedCount ? "stale" : "muted") : "stale",
    canSave: Boolean(settings.available),
    unavailableReason: unavailable,
    summaryCards: [
      {
        label: "Qualification",
        value: settings.available ? `${capturedCount}/${totalCount}` : "Locked",
        detail: latestQualification ? "Latest qualification is visible to managers and owners." : unavailable,
        tone: settings.available ? (capturedCount ? "ready" : "muted") : "stale",
      },
      {
        label: "Seller intent",
        value: cleanText(values.motivation, "Not captured"),
        detail: cleanText(values.timeline, "Timeline not captured"),
        tone: values.motivation || values.timeline ? "ready" : "muted",
      },
      {
        label: "Closeout next step",
        value: cleanText(values.next_step, "Set after reply"),
        detail: cleanText(values.price_expectation, "Price expectation not captured"),
        tone: values.next_step ? "ready" : "stale",
      },
    ],
    rows: QUALIFICATION_FIELDS.map((field) => ({
      label: field.label,
      value: cleanText(values[field.key], "Not captured"),
      tone: values[field.key] ? "ready" : "muted",
      meta: field.placeholder,
    })),
  };
}

export function buildOperatorReplyAssist(options = {}) {
  const settings = Object.assign({ conversation: null, qualification: null }, options);
  const conversation = settings.conversation || {};
  const qualification = settings.qualification || {};
  const contactName = cleanText(conversation.contact_name, "there").split(" ")[0];
  const nextStep = cleanText(qualification.next_step, "talk through next steps");
  const motivation = cleanText(qualification.motivation, "your property");
  return {
    title: "Reply assist",
    detail: "Sandbox-safe snippets operators can insert, review, and edit before queueing a reply.",
    templates: [
      {
        key: "quick_ack",
        label: "Quick acknowledgement",
        body: `Hi ${contactName}, thanks for getting back to us. I can help with ${motivation}. What is the best time today to talk for a few minutes?`,
      },
      {
        key: "qualification_follow_up",
        label: "Qualification follow-up",
        body: `Hi ${contactName}, I noted your next step as ${nextStep}. Can you share any must-know details about condition, timing, or price expectations before we review the file?`,
      },
      {
        key: "manager_review",
        label: "Manager review",
        body: `Hi ${contactName}, I am going to review this with our team and follow up with the clearest next step. If anything changes with timing or price, send it here.`,
      },
    ],
  };
}

export function buildOperatorCloseoutChecklist(options = {}) {
  const settings = Object.assign(
    {
      qualification: null,
      messages: [],
      tasks: [],
      latestMutation: null,
      recentActions: [],
    },
    options
  );
  const qualification = settings.qualification || {};
  const latestMutation = settings.latestMutation || null;
  const messages = Array.isArray(settings.messages) ? settings.messages : [];
  const tasks = Array.isArray(settings.tasks) ? settings.tasks : [];
  const recentActions = Array.isArray(settings.recentActions) ? settings.recentActions : [];
  const replyQueued = messages.some((item) => String(item.direction || "").toLowerCase() === "outbound") || latestMutation?.kind === "reply";
  const outcomeSaved = latestMutation?.kind === "disposition";
  const followUpPlanned = tasks.some((item) => item.status !== "completed") || Boolean(latestMutation?.taskIdCreated);
  const qualificationReady = (qualification.capturedCount || 0) >= 4;
  const managerVisible = recentActions.length > 0 || ["reply", "disposition", "qualification", "task", "note"].includes(String(latestMutation?.kind || ""));
  const items = [
    { label: "Qualification captured", complete: qualificationReady, detail: `${qualification.capturedCount || 0}/${qualification.totalCount || 5} fields` },
    { label: "Sandbox reply queued", complete: replyQueued, detail: replyQueued ? "Outbound reply visible in thread" : "Insert a template or write a custom reply" },
    { label: "Disposition selected", complete: outcomeSaved, detail: outcomeSaved ? "Outcome saved for the record" : "Save interested, not interested, wrong number, or DNC" },
    { label: "Follow-up planned", complete: followUpPlanned, detail: followUpPlanned ? "Reminder coverage exists" : "Create a reminder for the next touch" },
    { label: "Manager/owner visibility", complete: managerVisible, detail: managerVisible ? "Latest work is visible in activity" : "Save qualification, reply, or disposition" },
  ];
  return {
    completedCount: items.filter((item) => item.complete).length,
    totalCount: items.length,
    statusLabel: items.every((item) => item.complete) ? "Closeout ready" : "Closeout in progress",
    tone: items.every((item) => item.complete) ? "ready" : "stale",
    items: items.map((item) => ({
      ...item,
      value: item.complete ? "Done" : "Open",
      tone: item.complete ? "ready" : "stale",
    })),
  };
}

function workflowStepByKey(workflowGuide, key) {
  return (Array.isArray(workflowGuide?.steps) ? workflowGuide.steps : []).find((item) => item.key === key) || null;
}

function focusStage(workflowGuide, key, label, options = {}) {
  const workflowStep = workflowStepByKey(workflowGuide, key);
  const status = workflowStep?.status || "pending";
  const complete = status === "complete";
  const active = status === "active";
  const locked = Boolean(options.locked);
  return {
    key,
    label,
    value: locked ? "Locked" : complete ? "Done" : active ? "Now" : workflowStep?.value || "Open",
    detail: locked ? options.lockedDetail : workflowStep?.meta || options.detail || "",
    tone: locked ? "overdue" : complete ? "ready" : active ? "stale" : "muted",
    status,
  };
}

export function buildOperatorFocusPath(options = {}) {
  const settings = Object.assign(
    {
      workflowGuide: null,
      messagingSafety: null,
      closeoutChecklist: null,
    },
    options
  );
  const workflowGuide = settings.workflowGuide || {};
  const messagingSafety = settings.messagingSafety || {};
  const closeoutChecklist = settings.closeoutChecklist || {};
  const ready =
    workflowGuide.status === "ready"
    || (
      Number(closeoutChecklist.completedCount || 0) > 0
      && Number(closeoutChecklist.completedCount || 0) === Number(closeoutChecklist.totalCount || 0)
    );
  const activeStep = (Array.isArray(workflowGuide.steps) ? workflowGuide.steps : []).find((item) => item.status === "active");
  const nextStep = activeStep || (Array.isArray(workflowGuide.steps) ? workflowGuide.steps : []).find((item) => item.status === "pending");
  const replyLocked = Boolean(messagingSafety.replyLocked);

  return {
    title: "Today's lead path",
    statusLabel: ready ? "Ready for next lead" : workflowGuide.primaryAction || "Continue lead",
    tone: ready ? "ready" : nextStep ? "stale" : "muted",
    detail: ready
      ? "Reply, outcome, follow-up, and manager visibility are complete for this lead."
      : nextStep
        ? `${nextStep.label}: ${nextStep.meta}`
        : "Lead progress is waiting for workspace activity.",
    stages: [
      focusStage(workflowGuide, "reply", "Reply", {
        locked: replyLocked,
        lockedDetail: messagingSafety.replyLockReason || "Messaging safety is blocking replies.",
      }),
      focusStage(workflowGuide, "outcome", "Outcome", {
        detail: "Disposition has not been saved yet.",
      }),
      focusStage(workflowGuide, "follow_up", "Follow-up", {
        detail: "Reminder coverage has not been created yet.",
      }),
      focusStage(workflowGuide, "visibility", "Visibility", {
        detail: "Manager and owner visibility updates after a saved action.",
      }),
    ],
  };
}

function targetForDailyStep(stepKey, targetId) {
  if (stepKey === "reply") {
    return `reply-form-${targetId}`;
  }
  if (stepKey === "outcome") {
    return `disposition-form-${targetId}`;
  }
  if (stepKey === "follow_up") {
    return "follow-up-workspace-panel";
  }
  if (stepKey === "visibility") {
    return "operator-next-lead-handoff";
  }
  return "operator-focus-path";
}

function actionForDailyStep(step, targetId, messagingSafety) {
  if (messagingSafety?.replyLocked && step?.key === "reply") {
    return {
      label: "Review safety",
      targetId: "operator-messaging-safety",
      tone: "overdue",
    };
  }
  const labels = {
    reply: "Write reply",
    outcome: "Save outcome",
    follow_up: "Plan follow-up",
    visibility: "Review closeout",
  };
  return {
    label: labels[step?.key] || "Continue lead",
    targetId: targetForDailyStep(step?.key, targetId),
    tone: step?.tone || (step?.status === "active" ? "stale" : "muted"),
  };
}

export function buildOperatorDailyBrief(options = {}) {
  const settings = Object.assign(
    {
      conversation: null,
      workflowGuide: null,
      messagingSafety: null,
      closeoutChecklist: null,
      targetId: "operator-conversation-detail",
    },
    options
  );
  const conversation = settings.conversation || {};
  const workflowGuide = settings.workflowGuide || {};
  const messagingSafety = settings.messagingSafety || {};
  const closeoutChecklist = settings.closeoutChecklist || {};
  const steps = Array.isArray(workflowGuide.steps) ? workflowGuide.steps : [];
  const activeStep = steps.find((item) => item.status === "active") || steps.find((item) => item.status === "pending") || null;
  const primaryAction = actionForDailyStep(activeStep, settings.targetId, messagingSafety);
  const completedCount = numberValue(closeoutChecklist.completedCount, 0);
  const totalCount = numberValue(closeoutChecklist.totalCount, 0) || 5;
  const done = workflowGuide.status === "ready" || (totalCount > 0 && completedCount >= totalCount);
  const firstName = cleanText(conversation.contact_name, "Current lead").split(" ")[0];
  const actionRows = steps
    .filter((step) => step.status !== "complete")
    .slice(0, 3)
    .map((step) => {
      const action = actionForDailyStep(step, settings.targetId, messagingSafety);
      return {
        label: step.label,
        value: step.status === "active" ? "Now" : "Next",
        tone: action.tone,
        detail: step.meta || "Continue this workflow step.",
        actionLabel: action.label,
        targetId: action.targetId,
      };
    });

  return {
    title: "Daily work brief",
    statusLabel: done ? "Lead ready to close out" : primaryAction.label,
    tone: done ? "ready" : primaryAction.tone,
    detail: done
      ? `${firstName}'s reply, outcome, follow-up, and visibility are ready for the next lead handoff.`
      : `${firstName}'s current lead path is focused on ${cleanText(activeStep?.label, "the next action").toLowerCase()}.`,
    primaryAction,
    cards: [
      {
        label: "Current lead",
        value: cleanText(conversation.contact_name, "Unknown contact"),
        detail: `${cleanText(conversation.phone_e164, "phone unavailable")} | ${cleanText(conversation.aging_bucket, "queue status unknown")}`,
        tone: conversation.first_response_overdue ? "overdue" : "ready",
      },
      {
        label: "Next action",
        value: done ? "Open next lead" : cleanText(activeStep?.label, workflowGuide.primaryAction || "Continue lead"),
        detail: done ? "Closeout is complete." : cleanText(activeStep?.meta, "Use the focused workflow below."),
        tone: done ? "ready" : primaryAction.tone,
      },
      {
        label: "Reply state",
        value: messagingSafety.statusLabel || "Sandbox ready",
        detail: messagingSafety.replyLocked
          ? messagingSafety.replyLockReason || "Messaging safety is blocking replies."
          : "Sandbox/test messaging guard is active.",
        tone: messagingSafety.replyLocked ? "overdue" : "ready",
      },
      {
        label: "Closeout",
        value: `${completedCount}/${totalCount}`,
        detail: closeoutChecklist.statusLabel || "Closeout in progress",
        tone: done ? "ready" : completedCount > 0 ? "stale" : "muted",
      },
    ],
    actionRows: actionRows.length
      ? actionRows
      : [
          {
            label: "Ready for next lead",
            value: "Done",
            tone: "ready",
            detail: "The current lead has the required reply, outcome, follow-up, and visibility trail.",
            actionLabel: "Open handoff",
            targetId: "operator-next-lead-handoff",
          },
        ],
  };
}

export function buildOperatorQueueCommand(options = {}) {
  const settings = Object.assign(
    {
      triage: null,
      activeView: null,
      activeItems: [],
      mine: [],
      unassigned: [],
    },
    options
  );
  const triage = settings.triage || {};
  const summary = triage.summary || {};
  const activeView = settings.activeView || {};
  const activeItems = Array.isArray(settings.activeItems) ? settings.activeItems : [];
  const mine = Array.isArray(settings.mine) ? settings.mine : [];
  const unassigned = Array.isArray(settings.unassigned) ? settings.unassigned : [];
  const focusQueue = cleanText(triage.focus_queue, "mine");
  const assignedTotal = numberValue(summary.my_open_total, mine.length);
  const awaitingResponse = numberValue(summary.my_awaiting_response_total, 0);
  const unassignedTotal = numberValue(summary.unassigned_open_total, unassigned.length);
  const overdueUnassigned = numberValue(summary.unassigned_overdue_total, 0);
  const ready = activeItems.length > 0;
  const focusLabel = focusQueue === "idle" ? "mine" : focusQueue;
  const activeLabel = cleanText(activeView.label, focusLabel === "unassigned" ? "Available Leads" : "Assigned Leads");

  return {
    title: "Work one lead at a time",
    statusLabel: ready ? `${pluralize(activeItems.length, "lead")} ready` : "Queue clear",
    tone: ready ? (awaitingResponse > 0 || overdueUnassigned > 0 ? "stale" : "ready") : "muted",
    detail: ready
      ? `Start with ${activeLabel.toLowerCase()}, then move through reply, outcome, follow-up, and visibility before opening another lead.`
      : "No lead is waiting in this lane. Switch queues or refresh when new work arrives.",
    primaryActionLabel: ready ? "Open next lead" : "Review queue slices",
    cards: [
      {
        label: "Next move",
        value: ready ? "Open next lead" : "No lead waiting",
        detail: ready ? "Loads the conversation workspace and keeps the work path in one place." : "Use another queue slice when this lane is empty.",
        tone: ready ? "ready" : "muted",
      },
      {
        label: "Assigned work",
        value: pluralize(assignedTotal, "lead"),
        detail: `${awaitingResponse} awaiting response in your assigned lane.`,
        tone: awaitingResponse > 0 ? "stale" : assignedTotal > 0 ? "ready" : "muted",
      },
      {
        label: "Available work",
        value: pluralize(unassignedTotal, "lead"),
        detail: overdueUnassigned > 0 ? `${overdueUnassigned} unassigned overdue.` : "No unassigned SLA pressure visible.",
        tone: overdueUnassigned > 0 ? "overdue" : unassignedTotal > 0 ? "stale" : "muted",
      },
    ],
  };
}

export function buildOperatorNextLeadHandoff(options = {}) {
  const settings = Object.assign(
    {
      closeoutChecklist: null,
      focusPath: null,
      nextConversationId: null,
    },
    options
  );
  const checklist = settings.closeoutChecklist || {};
  const completed = numberValue(checklist.completedCount, 0);
  const total = numberValue(checklist.totalCount, 0);
  const complete = total > 0 && completed >= total;
  const nextConversationId = cleanText(settings.nextConversationId, "");
  return {
    title: complete ? "Lead complete" : "Finish this lead",
    statusLabel: complete ? "Ready for next lead" : `${completed}/${total || 5} complete`,
    tone: complete ? "ready" : completed > 0 ? "stale" : "muted",
    detail: complete
      ? "Reply, outcome, follow-up, and manager visibility are in place. Move to the next assigned lead when ready."
      : "Keep the operator path tight: reply, disposition, reminder, and visibility before moving on.",
    primaryActionLabel: complete && nextConversationId ? "Open next lead" : complete ? "Back to assigned leads" : "Keep working this lead",
    secondaryActionLabel: "Back to queue",
    nextConversationId,
    complete,
  };
}
